import { useCallback } from 'react'
import { Controller, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form'
import { ParseableDate } from '@mui/lab/internal/pickers/constants/prop-types'
import { BasePickerProps } from '@mui/lab/internal/pickers/typings/BasePicker'
import { ErrorMessage } from '../ErrorMessage'
import { HintInput } from '../../inputs/HintInput'

type TimePickerProps = BasePickerProps<ParseableDate<Date>, Date | null> & {
  error?: boolean
  helperText?: string
  label: string
  name: string
  onChange?: (date: Date | null, keyboardInputValue?: string | undefined) => void
  onBlur?: () => void
  onFocus?: () => void
  value: Date | null
}

type NullableDate = Date | null

type Props = Omit<TimePickerProps, 'value'> & {
  defaultValue?: Date
}

type Field = ControllerRenderProps<FieldValues, string>

const TimePicker = ({ defaultValue, name, onChange: onChangeProp, onFocus, ...rest }: Props) => {
  const {
    control,
    formState: { errors },
  } = useFormContext()

  const error = errors?.[name]
  const hasError = Boolean(error)

  const render = ({ field }: { field: Field }) => {
    const { onChange: onChangeFormHandler } = field

    const onChangeProxy = useCallback(
      (date: NullableDate, keyboardInputValue?: string | undefined) => {
        if (onChangeProp) {
          onChangeProp(date, keyboardInputValue)
        }

        onChangeFormHandler(date)
      },
      [onChangeFormHandler, onChangeProp]
    )

    return (
      <>
        <HintInput {...rest} {...field} hasError={hasError} onChange={onChangeProxy} onFocus={onFocus} />
        {hasError && <ErrorMessage message={error.message} />}
      </>
    )
  }

  return <Controller defaultValue={defaultValue} control={control} name={name} render={render} />
}

export { TimePicker }
